import React, { useState, useEffect, useRef } from 'react';

const VALID_VOICES = [
  "Achernar XA", "Achird XA",
  "Jamal", "Mouna",
  "Achernar US", "Achird US", "Aoede US", "Charon US", "Kore US", "Orus US", "Puck US",
  "Zephyr US", "Leda US", "Despina US", "Enceladus US",
  "Ahmed", "Farida"
];

const AudioManagerAdmin = ({ API, adminToken, isDarkMode, showNotification, tab }) => {
  // Audio Manager State
  const [audioSource, setAudioSource] = useState('translations');
  const [audioFilter, setAudioFilter] = useState('all');
  const [audioSearch, setAudioSearch] = useState('');
  const [audioSearchDebounced, setAudioSearchDebounced] = useState('');
  const [audioPage, setAudioPage] = useState(1);
  const [audioWords, setAudioWords] = useState([]);
  const [audioSources, setAudioSources] = useState([]);
  const [audioTotalPages, setAudioTotalPages] = useState(1);
  const [audioTotal, setAudioTotal] = useState(0);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioGenerating, setAudioGenerating] = useState(null);
  const [audioDefaultVoice, setAudioDefaultVoice] = useState('Achernar XA');
  const [audioPerWordVoice, setAudioPerWordVoice] = useState({});
  const [audioPerWordSpeed, setAudioPerWordSpeed] = useState({});
  const [audioPerWordTtsText, setAudioPerWordTtsText] = useState({});
  const [audioPlayingId, setAudioPlayingId] = useState(null);
  const [audioUsage, setAudioUsage] = useState({ daily_count: 0, monthly_count: 0 });
  const [speechgenBalance, setSpeechgenBalance] = useState(null);
  const audioRef = useRef(null);

  // SpeechGen tab state
  const [speechgenToken, setSpeechgenToken] = useState('');
  const [speechgenEmail, setSpeechgenEmail] = useState('');
  const [speechgenTokenSet, setSpeechgenTokenSet] = useState(false);
  const [speechgenTokenMasked, setSpeechgenTokenMasked] = useState('');
  const [speechgenDefaultVoice, setSpeechgenDefaultVoice] = useState('Achernar XA');
  const [speechgenVoices, setSpeechgenVoices] = useState([]);
  const [speechgenLoading, setSpeechgenLoading] = useState(false);

  // ── SpeechGen Functions ──

  const loadSpeechgenSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/speechgen/settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSpeechgenTokenSet(data.token_set);
        setSpeechgenTokenMasked(data.token_masked);
        setSpeechgenEmail(data.email || '');
        setSpeechgenDefaultVoice(data.default_voice || 'Achernar XA');
        setSpeechgenBalance(data.balance);
      }
    } catch (error) {
      console.error('Error loading SpeechGen settings:', error);
    }
  };

  const saveSpeechgenSettings = async (updates) => {
    setSpeechgenLoading(true);
    try {
      const response = await fetch(`${API}/admin/speechgen/settings`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        showNotification('SpeechGen settings saved', 'success');
        loadSpeechgenSettings();
      } else {
        showNotification('Failed to save SpeechGen settings', 'error');
      }
    } catch (error) {
      showNotification('Failed to save SpeechGen settings', 'error');
    } finally {
      setSpeechgenLoading(false);
    }
  };

  const removeSpeechgenToken = async () => {
    try {
      const response = await fetch(`${API}/admin/speechgen/settings`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        showNotification('SpeechGen token removed', 'success');
        setSpeechgenTokenSet(false);
        setSpeechgenTokenMasked('');
        setSpeechgenBalance(null);
      }
    } catch (error) {
      showNotification('Failed to remove token', 'error');
    }
  };

  const loadSpeechgenVoices = async () => {
    try {
      const response = await fetch(`${API}/admin/speechgen/voices`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSpeechgenVoices(data);
      }
    } catch (error) {
      console.error('Error loading SpeechGen voices:', error);
    }
  };

  // ── Audio Manager Functions ──

  const loadAudioUsage = async () => {
    try {
      const response = await fetch(`${API}/admin/audio/usage`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAudioUsage(data);
      }
    } catch (error) {
      console.error('Error loading audio usage:', error);
    }
  };

  const loadAudioSources = async () => {
    try {
      const response = await fetch(`${API}/admin/audio/sources`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAudioSources(data);
      }
    } catch (error) {
      console.error('Error loading audio sources:', error);
    }
  };

  const loadAudioWords = async (src, flt, srch, pg) => {
    setAudioLoading(true);
    try {
      const params = new URLSearchParams({
        source: src || audioSource,
        filter: flt || audioFilter,
        search: srch !== undefined ? srch : audioSearchDebounced,
        page: pg || audioPage,
        per_page: 50
      });
      const response = await fetch(`${API}/admin/audio/words?${params}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAudioWords(data.words || []);
        setAudioTotal(data.total || 0);
        setAudioTotalPages(data.pages || 1);
      }
    } catch (error) {
      console.error('Error loading audio words:', error);
    } finally {
      setAudioLoading(false);
    }
  };

  const handleAudioGenerate = async (word) => {
    const wordId = word.id;
    setAudioGenerating(wordId);
    try {
      const voice = audioPerWordVoice[wordId] || audioDefaultVoice;
      const speed = audioPerWordSpeed[wordId]; // undefined = auto (server picks)
      const ttsText = audioPerWordTtsText[wordId] || ''; // empty = use original
      const response = await fetch(`${API}/admin/audio/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          source_type: audioSource,
          source_id: wordId,
          darija_text: word.darija || '',
          arabic_text: word.arabic || '',
          english_text: word.english || '',
          voice: voice,
          speed: speed,
          tts_text: ttsText
        })
      });
      if (response.ok) {
        const data = await response.json();
        showNotification(`Audio generated for "${word.darija}" (${data.voice})`, 'success');
        // Auto-play the generated audio
        if (data.audio_url) {
          const audioEl = audioRef.current;
          if (audioEl) {
            audioEl.src = `${API}${data.audio_url}`;
            audioEl.play().catch(() => {});
            setAudioPlayingId(wordId);
          }
        }
        // Update just this word's audio status in-place (no full reload)
        setAudioWords(prev => prev.map(w =>
          w.id === wordId
            ? { ...w, audio: { audio_id: data.audio_id, status: 'pending', voice: data.voice, file_name: data.file_name, file_path: data.file_path, duration_ms: data.duration_ms } }
            : w
        ));
        loadAudioUsage();
        loadSpeechgenSettings();
      } else if (response.status === 429) {
        const err = await response.json().catch(() => ({}));
        showNotification(err.detail || 'Audio generation limit reached', 'error');
        loadAudioUsage();
      } else {
        const err = await response.json().catch(() => ({}));
        showNotification(err.detail || 'Failed to generate audio', 'error');
      }
    } catch (error) {
      console.error('Error generating audio:', error);
      showNotification('Failed to generate audio', 'error');
    } finally {
      setAudioGenerating(null);
    }
  };

  const handleAudioApprove = async (audioId) => {
    try {
      const response = await fetch(`${API}/admin/audio/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ audio_id: audioId })
      });
      if (response.ok) {
        showNotification('Audio approved!', 'success');
        setAudioWords(prev => prev.map(w =>
          w.audio && w.audio.audio_id === audioId
            ? { ...w, audio: { ...w.audio, status: 'approved' } }
            : w
        ));
      } else {
        showNotification('Failed to approve audio', 'error');
      }
    } catch (error) {
      showNotification('Failed to approve audio', 'error');
    }
  };

  const handleAudioReject = async (audioId) => {
    try {
      const response = await fetch(`${API}/admin/audio/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ audio_id: audioId })
      });
      if (response.ok) {
        showNotification('Audio rejected and deleted', 'success');
        setAudioWords(prev => prev.map(w =>
          w.audio && w.audio.audio_id === audioId
            ? { ...w, audio: null }
            : w
        ));
      } else {
        showNotification('Failed to reject audio', 'error');
      }
    } catch (error) {
      showNotification('Failed to reject audio', 'error');
    }
  };

  const handleAudioDelete = async (audioId) => {
    try {
      // First unlock, then delete
      await fetch(`${API}/admin/audio/unlock/${audioId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const response = await fetch(`${API}/admin/audio/${audioId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        showNotification('Audio deleted', 'success');
        setAudioWords(prev => prev.map(w =>
          w.audio && w.audio.audio_id === audioId
            ? { ...w, audio: null }
            : w
        ));
      } else {
        const err = await response.json().catch(() => ({}));
        showNotification(err.detail || 'Failed to delete audio', 'error');
      }
    } catch (error) {
      showNotification('Failed to delete audio', 'error');
    }
  };

  const handleAudioPlay = (word) => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    // Toggle pause if already playing this word
    if (audioPlayingId === word.id) {
      audioEl.pause();
      setAudioPlayingId(null);
      return;
    }
    if (word.audio && word.audio.audio_id) {
      audioEl.src = `${API}/audio/stream/${word.audio.audio_id}`;
      audioEl.play().catch(() => {});
      setAudioPlayingId(word.id);
    }
  };

  const handleAudioSaveDefaultVoice = async (voice) => {
    setAudioDefaultVoice(voice);
    try {
      await fetch(`${API}/admin/audio/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ defaultVoice: voice })
      });
    } catch (error) {
      console.error('Error saving default voice:', error);
    }
  };

  // ── useEffects ──

  // Debounce audio search
  useEffect(() => {
    const timer = setTimeout(() => {
      setAudioSearchDebounced(audioSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [audioSearch]);

  // Load audio data when tab/source/filter/search/page changes
  useEffect(() => {
    if (tab === 'audio-manager') {
      loadAudioSources();
      loadAudioWords(audioSource, audioFilter, audioSearchDebounced, audioPage);
      loadAudioUsage();
      loadSpeechgenSettings();
    }
    if (tab === 'speechgen') {
      loadSpeechgenSettings();
      loadSpeechgenVoices();
    }
  }, [tab, audioSource, audioFilter, audioSearchDebounced, audioPage]);

  // ── Render ──

  return (
    <>
      {tab === 'speechgen' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              🎙️ SpeechGen Settings
            </h2>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              speechgenTokenSet ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {speechgenTokenSet ? 'Connected' : 'Not Configured'}
            </span>
          </div>

          {/* API Token card */}
          <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>API Configuration</h3>
            {speechgenTokenSet ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Current Token:</span>
                  <span className={`font-mono text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{speechgenTokenMasked}</span>
                  <button onClick={removeSpeechgenToken} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700">
                    Remove Token
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Email:</span>
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{speechgenEmail}</span>
                </div>
                {speechgenBalance !== null && (
                  <div className="flex items-center gap-3">
                    <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Balance:</span>
                    <span className={`text-sm font-bold ${
                      speechgenBalance <= 0 ? 'text-red-500' : isDarkMode ? 'text-green-400' : 'text-green-600'
                    }`}>
                      {Number(speechgenBalance).toLocaleString()} characters remaining
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    API Token (from speechgen.io profile)
                  </label>
                  <input
                    type="password"
                    value={speechgenToken}
                    onChange={(e) => setSpeechgenToken(e.target.value)}
                    placeholder="Your SpeechGen secret token"
                    className={`w-full rounded-lg px-3 py-2 border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Account Email
                  </label>
                  <input
                    type="email"
                    value={speechgenEmail}
                    onChange={(e) => setSpeechgenEmail(e.target.value)}
                    placeholder="your@email.com"
                    className={`w-full rounded-lg px-3 py-2 border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'}`}
                  />
                </div>
                <button
                  onClick={() => saveSpeechgenSettings({ token: speechgenToken, email: speechgenEmail })}
                  disabled={!speechgenToken.trim() || !speechgenEmail.trim() || speechgenLoading}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {speechgenLoading ? 'Saving...' : 'Save Token & Email'}
                </button>
              </div>
            )}
          </div>

          {/* Default Voice card */}
          <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Default Voice</h3>
            <div className="flex items-center gap-4">
              <select
                value={speechgenDefaultVoice}
                onChange={(e) => setSpeechgenDefaultVoice(e.target.value)}
                className={`rounded-lg px-3 py-2 border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
              >
                {speechgenVoices.length > 0 ? speechgenVoices.map(v => (
                  <option key={v.id} value={v.id}>{v.label} ({v.gender})</option>
                )) : VALID_VOICES.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
              <button
                onClick={() => saveSpeechgenSettings({ default_voice: speechgenDefaultVoice })}
                disabled={speechgenLoading}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Save Default Voice
              </button>
            </div>
          </div>

          {/* Info Card */}
          <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
            <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>About SpeechGen</h3>
            <div className={`text-sm space-y-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <p>SpeechGen.io provides Text-to-Speech with 1000+ voices in 150+ languages.</p>
              <p>Recommended voice for Darija: <strong>Achernar XA</strong> (Arabic HD, Google Chirp3)</p>
              <p>For best pronunciation, Audio Manager sends Arabic script text to SpeechGen.</p>
              <p>One-time payment — no monthly charges, no surprise bills.</p>
            </div>
          </div>
        </div>
      )}

      {tab === 'audio-manager' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              🔊 Audio Manager
            </h2>
          </div>

          {/* Stats Bar */}
          <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
            <div className="flex flex-wrap items-center gap-6">
              {(() => {
                const currentSource = audioSources.find(s => s.id === audioSource);
                return (
                  <>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total:</span>
                      <span className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        {currentSource ? currentSource.total.toLocaleString() : '—'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-500">✅</span>
                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Approved: {currentSource ? currentSource.hasAudio : 0}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-yellow-500">⏳</span>
                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Pending: {currentSource ? currentSource.pending : 0}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-red-500">❌</span>
                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        No Audio: {currentSource ? currentSource.noAudio : 0}
                      </span>
                    </div>
                  </>
                );
              })()}
              <div className="ml-auto flex items-center gap-2">
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Default Voice:</span>
                <select
                  value={audioDefaultVoice}
                  onChange={(e) => {
                    setAudioDefaultVoice(e.target.value);
                    handleAudioSaveDefaultVoice(e.target.value);
                  }}
                  className={`text-sm rounded-lg px-3 py-1.5 border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                >
                  {VALID_VOICES.map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Usage Stats Bar */}
          <div className={`rounded-xl p-3 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
            <div className="flex flex-wrap items-center gap-6">
              <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Usage:</span>
              <span className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                Today: {audioUsage.daily_count}
              </span>
              <span className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                This Month: {audioUsage.monthly_count}
              </span>
              {speechgenBalance !== null && (
                <span className={`text-sm font-medium ${
                  speechgenBalance <= 0 ? 'text-red-500' : isDarkMode ? 'text-blue-400' : 'text-blue-600'
                }`}>
                  Balance: {Number(speechgenBalance).toLocaleString()} chars
                </span>
              )}
            </div>
          </div>

          {/* Controls Row: Source + Filter + Search */}
          <div className="flex flex-wrap items-start gap-4">
            {/* Source Dropdown */}
            <div className="w-56">
              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Source</label>
              <select
                value={audioSource}
                onChange={(e) => { setAudioSource(e.target.value); setAudioPage(1); }}
                className={`w-full rounded-lg px-3 py-2 border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
              >
                {audioSources.map(s => (
                  <option key={s.id} value={s.id}>{s.label} ({s.total.toLocaleString()})</option>
                ))}
              </select>
            </div>

            {/* Filter Buttons */}
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Filter</label>
              <div className={`flex rounded-lg overflow-hidden border ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                {[
                  { value: 'all', label: 'All' },
                  { value: 'no_audio', label: 'No Audio' },
                  { value: 'has_audio', label: 'Has Audio' },
                  { value: 'pending', label: 'Pending' },
                ].map(f => (
                  <button
                    key={f.value}
                    onClick={() => { setAudioFilter(f.value); setAudioPage(1); }}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      audioFilter === f.value
                        ? 'bg-blue-600 text-white'
                        : isDarkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Search Box */}
            <div className="flex-1 min-w-[200px]">
              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Search</label>
              <input
                type="text"
                value={audioSearch}
                onChange={(e) => setAudioSearch(e.target.value)}
                placeholder="Search words..."
                className={`w-full rounded-lg px-3 py-2 border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'}`}
              />
            </div>
          </div>

          {/* Word List */}
          {audioLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className={`ml-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading words...</span>
            </div>
          ) : audioWords.length === 0 ? (
            <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <div className="text-4xl mb-3">🔇</div>
              <p>No words found for the selected filter.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {audioWords.map((word, idx) => {
                const wordId = word.id;
                const wordVoice = audioPerWordVoice[wordId] || audioDefaultVoice;
                const audioStatus = word.audio ? word.audio.status : 'none';
                const audioId = word.audio ? word.audio.audio_id : null;
                const pageOffset = (audioPage - 1) * 50;

                return (
                  <div key={wordId} className={`rounded-xl p-4 border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
                    {/* Word Info */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className={`text-xs font-mono ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          #{pageOffset + idx + 1}
                        </span>
                        <div className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <span className="font-medium">English:</span> {word.english || '—'}
                        </div>
                        <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <span className="font-medium">Darija:</span> {word.darija || '—'}
                        </div>
                        <div className={`mt-1 ${word.arabic ? 'text-base' : 'text-sm'} ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <span className="font-medium">Arabic:</span>{' '}
                          {word.arabic ? (
                            <span dir="rtl" className={`font-semibold text-lg ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                              {word.arabic}
                            </span>
                          ) : (
                            <span className={`italic text-xs ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                              No Arabic — will use Latin for TTS
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        {audioStatus === 'none' && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            ❌ No Audio
                          </span>
                        )}
                        {audioStatus === 'pending' && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                            ⏳ Pending
                          </span>
                        )}
                        {audioStatus === 'approved' && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            ✅ Approved
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className={`flex flex-wrap items-center gap-2 pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      {/* Voice + Speed pickers */}
                      {audioStatus !== 'approved' && (
                        <>
                          <select
                            value={wordVoice}
                            onChange={(e) => setAudioPerWordVoice(prev => ({ ...prev, [wordId]: e.target.value }))}
                            className={`text-xs rounded-lg px-2 py-1.5 border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                          >
                            {VALID_VOICES.map(v => (
                              <option key={v} value={v}>{v}</option>
                            ))}
                          </select>
                          <select
                            value={audioPerWordSpeed[wordId] || ''}
                            onChange={(e) => setAudioPerWordSpeed(prev => ({ ...prev, [wordId]: e.target.value ? parseFloat(e.target.value) : undefined }))}
                            className={`text-xs rounded-lg px-2 py-1.5 border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                          >
                            <option value="">Speed: Auto</option>
                            <option value="0.5">0.5x Slow</option>
                            <option value="0.6">0.6x</option>
                            <option value="0.7">0.7x</option>
                            <option value="0.8">0.8x</option>
                            <option value="0.85">0.85x</option>
                            <option value="0.9">0.9x</option>
                            <option value="1">1.0x Normal</option>
                            <option value="1.1">1.1x</option>
                            <option value="1.2">1.2x Fast</option>
                          </select>
                        </>
                      )}

                      {/* Editable TTS text */}
                      {audioStatus !== 'approved' && (
                        <input
                          type="text"
                          dir="auto"
                          placeholder={word.arabic || word.darija || 'TTS text...'}
                          value={audioPerWordTtsText[wordId] || ''}
                          onChange={(e) => setAudioPerWordTtsText(prev => ({ ...prev, [wordId]: e.target.value }))}
                          className={`text-sm rounded-lg px-2 py-1.5 border w-full mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'}`}
                        />
                      )}

                      {/* No Audio → Generate */}
                      {audioStatus === 'none' && (
                        <button
                          onClick={() => handleAudioGenerate(word)}
                          disabled={audioGenerating === wordId}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          {audioGenerating === wordId ? '⏳ Generating...' : '🔊 Generate'}
                        </button>
                      )}

                      {/* Pending → Play, Approve, Reject, Regenerate */}
                      {audioStatus === 'pending' && (
                        <>
                          <button onClick={() => handleAudioPlay(word)} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-600 text-white hover:bg-gray-700">
                            {audioPlayingId === wordId ? '⏸ Stop' : '▶ Play'}
                          </button>
                          <button onClick={() => handleAudioApprove(audioId)} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700">
                            ✅ Approve
                          </button>
                          <button onClick={() => handleAudioReject(audioId)} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700">
                            ❌ Reject
                          </button>
                          <button onClick={() => handleAudioGenerate(word)} disabled={audioGenerating === wordId} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-50">
                            {audioGenerating === wordId ? '⏳ ...' : '🔄 Regenerate'}
                          </button>
                        </>
                      )}

                      {/* Approved → Play, Regenerate, Delete */}
                      {audioStatus === 'approved' && (
                        <>
                          <button onClick={() => handleAudioPlay(word)} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-600 text-white hover:bg-gray-700">
                            {audioPlayingId === wordId ? '⏸ Stop' : '▶ Play'}
                          </button>
                          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Voice: {word.audio?.voice || '—'} • {word.audio?.duration_ms ? `${(word.audio.duration_ms / 1000).toFixed(1)}s` : '—'}
                          </span>
                          <select
                            value={wordVoice}
                            onChange={(e) => setAudioPerWordVoice(prev => ({ ...prev, [wordId]: e.target.value }))}
                            className={`text-xs rounded-lg px-2 py-1.5 border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                          >
                            {VALID_VOICES.map(v => (
                              <option key={v} value={v}>{v}</option>
                            ))}
                          </select>
                          <button onClick={() => handleAudioGenerate(word)} disabled={audioGenerating === wordId} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-50">
                            {audioGenerating === wordId ? '⏳ ...' : '🔄 Regenerate'}
                          </button>
                          <button onClick={() => handleAudioDelete(audioId)} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700">
                            🗑 Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {audioTotalPages > 1 && (
            <div className="flex items-center justify-center gap-4 pt-4">
              <button
                onClick={() => setAudioPage(p => Math.max(1, p - 1))}
                disabled={audioPage === 1}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  audioPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600 hover:text-white'
                } ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
              >
                ← Previous
              </button>
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Page {audioPage} of {audioTotalPages} ({audioTotal.toLocaleString()} words)
              </span>
              <button
                onClick={() => setAudioPage(p => Math.min(audioTotalPages, p + 1))}
                disabled={audioPage === audioTotalPages}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  audioPage === audioTotalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600 hover:text-white'
                } ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
              >
                Next →
              </button>
            </div>
          )}

          {/* Hidden audio element */}
          <audio ref={audioRef} onEnded={() => setAudioPlayingId(null)} style={{ display: 'none' }} />
        </div>
      )}
    </>
  );
};

export default AudioManagerAdmin;
